import { jsx as _jsx } from "react/jsx-runtime";
import { useMotionPresence } from '#pplx/frontend/aether/components/Motion/useMotionPresence.js';
import { SPRING_CONFIG_SNAPPY } from '#pplx/frontend/aether/config.js';
import { lazy, Suspense, useLayoutEffect, useRef, useState } from 'react';
const MotionExpandImpl = lazy(async () => {
    const { animated, useSpring } = await import('@react-spring/web');
    function MotionExpandImpl({ isVisible, children, className, direction = 'vertical', animateContentResize = true, }) {
        const { isPresent, animationState, isInitialEnter, handleAnimationEnd } = useMotionPresence(isVisible);
        const contentRef = useRef(null);
        const [contentSize, setContentSize] = useState(null);
        const isHorizontal = direction === 'horizontal';
        const sizeProperty = isHorizontal ? 'width' : 'height';
        // Measure content size and watch for changes
        useLayoutEffect(() => {
            const element = contentRef.current;
            if (!element || !isPresent) {
                return;
            }
            const updateSize = () => {
                const size = isHorizontal ? element.scrollWidth : element.scrollHeight;
                setContentSize(size);
            };
            // Initial measurement
            updateSize();
            // Observer to detect size changes
            const resizeObserver = new ResizeObserver(() => {
                updateSize();
            });
            resizeObserver.observe(element);
            return () => {
                resizeObserver.disconnect();
            };
        }, [isPresent, isHorizontal]);
        const targetSize = contentSize ?? 0;
        const isMeasured = contentSize !== null;
        const useNaturalSize = animationState === 'idle' && !animateContentResize;
        const springs = useSpring({
            to: animationState === 'exit'
                ? { [sizeProperty]: 0, opacity: 0 }
                : { [sizeProperty]: targetSize, opacity: 1 },
            from: { [sizeProperty]: 0, opacity: 0 },
            // Skip animation when first mounted with isVisible=true so the content
            // renders already-expanded instead of animating up from height: 0.
            immediate: !isMeasured || isInitialEnter || useNaturalSize,
            config: { ...SPRING_CONFIG_SNAPPY, clamp: true },
            // Defer the enter→idle transition until we have a measured target;
            // otherwise the spring rests at 0 on the unmeasured first frame and
            // burns through `isInitialEnter` before we can use it to skip.
            onRest: isMeasured ? handleAnimationEnd : undefined,
        });
        if (!isPresent) {
            return null;
        }
        return (_jsx(animated.div, { style: {
                overflow: animationState === 'idle' ? 'visible' : 'hidden',
                position: 'relative',
                ...springs,
                ...(useNaturalSize ? { [sizeProperty]: 'auto' } : undefined),
            }, className: className, children: _jsx("div", { ref: contentRef, children: children }) }));
    }
    return { default: MotionExpandImpl };
});
/**
 * Expand animation component with Suspense built-in.
 * Animates size from 0 to content size with fade effect.
 * Lazy loads react-spring only when used.
 */
export function MotionExpand(props) {
    // While react-spring loads, visible content renders statically instead of
    // popping in a frame late; hidden content stays hidden.
    return (_jsx(Suspense, { fallback: props.isVisible ? _jsx("div", { className: props.className, children: props.children }) : null, children: _jsx(MotionExpandImpl, { ...props }) }));
}
//# sourceMappingURL=MotionExpand.js.map